import {
  type FC,
  type FormEvent,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import styled from "styled-components";
import { useForm } from "react-hook-form";
import {
  Button,
  ButtonVariant,
  Checkbox,
  Dropdown,
  Item,
  ModalHeader,
  TextInput,
  Typography,
  TypographyVariant,
} from "../../../components/ui-kit";
import { TextInputErrorVariant } from "../../../components/ui-kit/input-fields/TextInput.interface";
import { PhoneFormField } from "../../../components/form-fields";
import { useModalManager } from "../../../context/ModalManager";
import {
  NotificationTypes,
  useNotification,
} from "../../../hooks/useNotification";
import { countries } from "../../../constants/countries";

export type EditFieldType =
  | "text"
  | "file"
  | "checkbox"
  | "phone"
  | "address-group"
  | "custom";

export interface PhoneFieldValue {
  number: string;
  code: string;
  countryCode: string;
}

export interface AddressGroupValue {
  addressLine1: string;
  addressLine2: string;
  city: string;
  country: string;
  zipCode: string;
  region: string;
}

export interface EditFieldConfig {
  name: string;
  label: string;
  type: EditFieldType;
  required?: boolean;
  regex?: RegExp;
  regexMessage?: string;
  maxLength?: number;
  inputMode?: "text" | "numeric";
  placeholder?: string;
  /** Sanitize raw input as the user types (type "text" only), e.g. strip non-digits. */
  transform?: (raw: string) => string;
  disabled?: boolean;
  hint?: string;
  /** type "checkbox" only: if false, renders the checkbox disabled. Defaults to enabled. */
  checkboxEnabled?: boolean;
  /** type "file" only: <input accept="..."> */
  fileAccept?: string;
  /** type "custom" only: render arbitrary UI for this field. */
  render?: (ctx: {
    value:
      | string
      | boolean
      | File
      | null
      | PhoneFieldValue
      | AddressGroupValue
      | undefined;
    setValue: (value: any) => void;
  }) => ReactNode;
}

export type EditFieldValues = Record<
  string,
  string | boolean | File | null | PhoneFieldValue | AddressGroupValue
>;

export interface DetailEditModalProps {
  title: string;
  fields: EditFieldConfig[];
  initialValues: EditFieldValues;
  /** Throw from here to keep the modal open (e.g. after showing your own error toast). */
  onSubmit: (values: EditFieldValues) => Promise<void>;
  submitLabel?: string;
  /** If set, clicking Save first shows a confirmation screen (with this title) before calling onSubmit. */
  confirmTitle?: string;
  confirmMessage?: string;
  /** Extra validation beyond per-field required/regex (e.g. "address group must be fully filled").
   * Return an error message to block submit (shown as a toast); return undefined to allow. */
  validate?: (values: EditFieldValues) => string | undefined;
}

const ModalWrap = styled.div`
  width: min(560px, 96vw);
  max-height: 90vh;
  overflow: auto;
  @media (max-width: 767px) {
    width: 100%;
    max-height: none;
    overflow: visible;
  }
`;

const Body = styled.div`
  padding: 20px 28px 28px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  @media (max-width: 767px) {
    padding: 16px 16px 24px;
  }
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const FileRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const AddressGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  @media (max-width: 767px) {
    grid-template-columns: 1fr;
  }
`;

const Hint = styled.span`
  font-size: 12px;
  color: #666;
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 8px;
  @media (max-width: 767px) {
    > * {
      flex: 1 1 0;
    }
  }
`;

const countryItems: Item[] = countries.map((c) => ({
  label: c.countryName,
  value: c.country,
}));

const emptyAddress: AddressGroupValue = {
  addressLine1: "",
  addressLine2: "",
  city: "",
  country: "",
  zipCode: "",
  region: "",
};

function validateFields(
  fields: EditFieldConfig[],
  values: EditFieldValues
): Record<string, string> {
  const errors: Record<string, string> = {};
  for (const field of fields) {
    if (field.type !== "text") continue;
    const raw = String(values[field.name] ?? "");
    if (field.required && !raw.trim()) {
      errors[field.name] = `${field.label} is required.`;
      continue;
    }
    if (field.regex && !field.regex.test(raw)) {
      errors[field.name] = field.regexMessage ?? `${field.label} is invalid.`;
    }
  }
  return errors;
}

function fieldsAreDirty(
  fields: EditFieldConfig[],
  values: EditFieldValues,
  initialValues: EditFieldValues
): boolean {
  return fields.some(
    (field) =>
      JSON.stringify(values[field.name]) !==
      JSON.stringify(initialValues[field.name])
  );
}

export const DetailEditModal: FC<DetailEditModalProps> = ({
  title,
  fields,
  initialValues,
  onSubmit,
  submitLabel = "Save",
  confirmTitle,
  confirmMessage = "Are you sure you want to save these changes?",
  validate,
}) => {
  const { removeLastModal } = useModalManager();
  const { showNotification } = useNotification();
  const [values, setValues] = useState<EditFieldValues>(initialValues);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [step, setStep] = useState<"form" | "confirm">("form");

  const phoneFields = useMemo(
    () => fields.filter((f) => f.type === "phone"),
    [fields]
  );
  const phoneForm = useForm<Record<string, PhoneFieldValue>>({
    defaultValues: phoneFields.reduce((acc, f) => {
      const iv = (initialValues[f.name] as PhoneFieldValue | undefined) ?? {
        number: "",
        code: "+44",
        countryCode: "GB",
      };
      acc[f.name] = iv;
      return acc;
    }, {} as Record<string, PhoneFieldValue>),
  });
  const watchedPhoneValues = phoneForm.watch();

  useEffect(() => {
    if (phoneFields.length === 0) return;
    setValues((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const f of phoneFields) {
        const wv = watchedPhoneValues[f.name];
        if (wv && JSON.stringify(wv) !== JSON.stringify(prev[f.name])) {
          next[f.name] = wv;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [JSON.stringify(watchedPhoneValues)]);

  useEffect(() => {
    setErrors(validateFields(fields, values));
  }, [values]);

  const setValue = (
    name: string,
    value: string | boolean | File | null | AddressGroupValue
  ) => {
    setValues((prev) => ({ ...prev, [name]: value }));
  };

  const hasErrors = Object.keys(errors).length > 0;
  const dirty = useMemo(
    () => fieldsAreDirty(fields, values, initialValues),
    [fields, values, initialValues]
  );

  const doSubmit = async () => {
    setSaving(true);
    try {
      await onSubmit(values);
      removeLastModal();
    } catch {
      // onSubmit is responsible for surfacing its own error (toast); keep the modal open so the user can retry.
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (hasErrors || !dirty) return;
    const validationError = validate?.(values);
    if (validationError) {
      showNotification(validationError, NotificationTypes.WARNING);
      return;
    }
    if (confirmTitle) {
      setStep("confirm");
      return;
    }
    void doSubmit();
  };

  if (step === "confirm") {
    return (
      <ModalWrap>
        <ModalHeader title={confirmTitle} handleClose={removeLastModal} />
        <Body>
          <Typography variant={TypographyVariant.BODY1}>
            {confirmMessage}
          </Typography>
          <Actions>
            <Button
              variant={ButtonVariant.OUTLINED}
              onClick={() => setStep("form")}
            >
              Back
            </Button>
            <Button
              variant={ButtonVariant.CONTAINED}
              loading={saving}
              onClick={() => void doSubmit()}
            >
              Confirm
            </Button>
          </Actions>
        </Body>
      </ModalWrap>
    );
  }

  return (
    <ModalWrap>
      <ModalHeader title={title} handleClose={removeLastModal} />
      <Body>
        <form onSubmit={handleSubmit}>
          {fields.map((field) => {
            if (field.type === "checkbox") {
              const enabled = field.checkboxEnabled ?? true;
              return (
                <Field key={field.name}>
                  <Checkbox
                    checked={Boolean(values[field.name])}
                    disabled={field.disabled || !enabled}
                    onChange={(e) => setValue(field.name, e.target.checked)}
                    label={
                      <Typography variant={TypographyVariant.BODY2}>
                        {field.label}
                      </Typography>
                    }
                  />
                  {field.hint && <Hint>{field.hint}</Hint>}
                </Field>
              );
            }
            if (field.type === "file") {
              return (
                <FileRow key={field.name}>
                  <Typography variant={TypographyVariant.BODY4}>
                    {field.label}
                  </Typography>
                  <input
                    type="file"
                    accept={field.fileAccept}
                    disabled={field.disabled}
                    onChange={(e) =>
                      setValue(field.name, e.target.files?.[0] ?? null)
                    }
                  />
                  {field.hint && <Hint>{field.hint}</Hint>}
                </FileRow>
              );
            }
            if (field.type === "phone") {
              return (
                <Field key={field.name}>
                  <PhoneFormField
                    name={`${field.name}.number`}
                    countryCodeName={`${field.name}.countryCode`}
                    phoneCodeName={`${field.name}.code`}
                    control={phoneForm.control}
                    label={field.label}
                    disabled={field.disabled}
                  />
                  {field.hint && <Hint>{field.hint}</Hint>}
                </Field>
              );
            }

            if (field.type === "custom") {
              return (
                <Field key={field.name}>
                  <Typography variant={TypographyVariant.BODY4}>
                    {field.label}
                  </Typography>
                  {field.render?.({
                    value: values[field.name],
                    setValue: (v) => setValue(field.name, v),
                  })}
                  {field.hint && <Hint>{field.hint}</Hint>}
                </Field>
              );
            }

            if (field.type === "address-group") {
              const addr =
                (values[field.name] as AddressGroupValue | undefined) ??
                emptyAddress;
              const setAddr = (patch: Partial<AddressGroupValue>) =>
                setValue(field.name, { ...addr, ...patch });
              return (
                <AddressGrid key={field.name}>
                  <Field>
                    <Typography variant={TypographyVariant.BODY4}>
                      Address line 1
                    </Typography>
                    <TextInput
                      value={addr.addressLine1}
                      onChange={(e) =>
                        setAddr({ addressLine1: e.target.value })
                      }
                    />
                  </Field>
                  <Field>
                    <Typography variant={TypographyVariant.BODY4}>
                      Address line 2
                    </Typography>
                    <TextInput
                      value={addr.addressLine2}
                      onChange={(e) =>
                        setAddr({ addressLine2: e.target.value })
                      }
                    />
                  </Field>
                  <Field>
                    <Typography variant={TypographyVariant.BODY4}>
                      City
                    </Typography>
                    <TextInput
                      value={addr.city}
                      onChange={(e) => setAddr({ city: e.target.value })}
                    />
                  </Field>
                  <Field>
                    <Typography variant={TypographyVariant.BODY4}>
                      Country
                    </Typography>
                    <Dropdown
                      fullWidth
                      items={countryItems}
                      defaultItem={
                        countryItems.find((i) => i.value === addr.country) ??
                        countryItems[0]
                      }
                      onChange={(item: Item) =>
                        setAddr({ country: String(item.value) })
                      }
                    />
                  </Field>
                  <Field>
                    <Typography variant={TypographyVariant.BODY4}>
                      ZIP / Postal code
                    </Typography>
                    <TextInput
                      value={addr.zipCode}
                      onChange={(e) => setAddr({ zipCode: e.target.value })}
                    />
                  </Field>
                  <Field>
                    <Typography variant={TypographyVariant.BODY4}>
                      Region (optional)
                    </Typography>
                    <TextInput
                      value={addr.region}
                      onChange={(e) => setAddr({ region: e.target.value })}
                    />
                  </Field>
                </AddressGrid>
              );
            }
            const raw = String(values[field.name] ?? "");
            return (
              <Field key={field.name}>
                <Typography variant={TypographyVariant.BODY4}>
                  {field.label}
                </Typography>
                <TextInput
                  value={raw}
                  placeholder={field.placeholder}
                  inputMode={field.inputMode}
                  maxLength={field.maxLength}
                  disabled={field.disabled}
                  onChange={(e) => {
                    const next = field.transform
                      ? field.transform(e.target.value)
                      : e.target.value;
                    setValue(field.name, next);
                  }}
                  error={errors[field.name]}
                  variant={
                    errors[field.name]
                      ? TextInputErrorVariant.ERROR
                      : TextInputErrorVariant.DEFAULT
                  }
                />
                {field.hint && <Hint>{field.hint}</Hint>}
              </Field>
            );
          })}
          <Actions>
            <Button
              type="button"
              variant={ButtonVariant.TEXT}
              onClick={removeLastModal}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant={ButtonVariant.CONTAINED}
              loading={saving}
              disabled={saving || hasErrors || !dirty}
            >
              {submitLabel}
            </Button>
          </Actions>
        </form>
      </Body>
    </ModalWrap>
  );
};
